import { useEffect, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarDays, Clock, Video, Star, Hourglass } from "lucide-react";
import { useApi, fmtDate, fmtTime } from "@/lib/api";
import { STATUS_STYLE } from "@/pages/MyAppointments";

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}><Icon className="h-5 w-5" /></div>
    <p className="font-display mt-4 text-3xl font-bold text-slate-800">{value}</p>
    <p className="text-sm font-semibold text-slate-500">{label}</p>
  </div>
);

export const AdminDashboard = () => {
  const api = useApi();
  const [s, setS] = useState(null);
  useEffect(() => { api("get", "/admin/stats").then(setS).catch(() => {}); }, [api]);
  if (!s) return <p className="text-slate-500">Loading...</p>;
  return (
    <div className="space-y-6" data-testid="admin-dashboard-stats">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat icon={CalendarDays} label="Total bookings" value={s.total} color="bg-teal-100 text-teal-700" testid="stat-total" />
        <Stat icon={Clock} label="Today" value={s.today} color="bg-sky-100 text-sky-700" testid="stat-today" />
        <Stat icon={Hourglass} label="Awaiting confirm" value={s.pending} color="bg-amber-100 text-amber-700" testid="stat-pending" />
        <Stat icon={Video} label="Online consults" value={s.online} color="bg-violet-100 text-violet-700" testid="stat-online" />
        <Stat icon={Star} label="Reviews to approve" value={s.pending_reviews} color="bg-rose-100 text-rose-700" testid="stat-pending-reviews" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-semibold">Upcoming appointments</h3>
          <div className="mt-4 divide-y divide-slate-100" data-testid="admin-upcoming-list">
            {s.upcoming.length === 0 && <p className="py-4 text-sm text-slate-500">Nothing upcoming yet.</p>}
            {s.upcoming.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-3 text-sm">
                <div><p className="font-bold text-slate-800">{b.child_name} <span className="font-normal text-slate-500">· {b.service}</span></p>
                  <p className="text-slate-500">{fmtDate(b.date)}, {fmtTime(b.time)} · {b.type === "online" ? "Online" : "Clinic"}</p></div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${STATUS_STYLE[b.status]}`}>{b.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-semibold">Bookings by service</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer>
              <BarChart data={s.by_service} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" allowDecimals={false} hide />
                <YAxis type="category" dataKey="service" width={150} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#0d9488" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
